/**
 * The receiver screen.
 *
 * The frame that holds the Chapter 1 receiver: the tuning knob, the readout, the signal meter and
 * the player's controls. Everything set here — knob position, volume, effects intensity — goes to
 * Postgres through the debounced sync and comes back on the next load, on any device.
 *
 * The front panel carries hardware only — the tuning knob, the power rocker, the volume fader and
 * the two pots — because a switch that would not be screwed to the front of a real set has no
 * business being screwed to the front of this one. Effects, ghost signals and captions are the
 * set's service settings: they live behind the gear in the header, in a modal that mirrors the
 * Save menu beside it.
 */
import { h } from '../dom.js';
import { t } from '../../i18n/index.js';
import { brandLogo, footer } from '../components/chrome.js';
import type { ProgressSync, EffectsIntensity } from '../../state/progress.js';
import type { Receiver } from '../../audio/receiver.js';
import { createKnob } from '../components/knob.js';
import { formatGameClock, type WorldClock } from '../../world/clock.js';
import type { LoadedPack } from '../../content/loader.js';
import type { ActiveSignal } from '../../world/matrix.js';
import type { Investigation } from '../../story/investigation.js';
import { createInvestigationPanel } from '../components/investigationPanel.js';
import { createOperatorCard } from '../components/operatorCard.js';
import { createSaveSlots } from '../components/saveSlots.js';
import { CaptionChannel, describeSignal } from '../../a11y/captions.js';
import { createDialoguePanel } from '../components/dialoguePanel.js';
import {
  PushToTalk,
  TransmitRules,
  createRecogniser,
  type TransmitOutcome,
  type TransmitState,
} from '../../voice/index.js';
import { api } from '../../api/client.js';
import type { Ghosts } from '../../app.js';
import type { SpeechPlayer } from '../../audio/speech.js';
import { FragmentRecorder, isCaptureSupported } from '../../ghosts/capture.js';
import { locale } from '../../i18n/index.js';
import type { MeResponse } from '../../api/types.js';
import type { View } from '../view.js';
import { refreshOverlay } from '../effects/overlay.js';

export interface RadioContext {
  me: MeResponse;
  sync: ProgressSync;
  receiver: Receiver;
  clock: WorldClock;
  /** Null when the content pack could not be loaded — the band is simply empty. */
  pack: LoadedPack | null;
  /** Null for a pack that tells no story (Chapter 2's sample). */
  investigation: Investigation | null;
  /** Null when the relay is unavailable — the band is simply empty. */
  ghosts: Ghosts | null;
  /** Plays authored NPC lines. Null when there is no audio stack. */
  speech: SpeechPlayer | null;
  /** Discard the live autosave (after confirm) and return to the Main Menu. */
  onAbandon: () => void;
  /** Load a manual slot into the game and enter it. The app handles adopt + rebuild + navigate. */
  onLoadSlot: (slot: number) => Promise<void>;
}

/** Fallback band when no pack is loaded. A pack's `band` overrides both. */
export const BAND_MIN_KHZ = 3000;
export const BAND_MAX_KHZ = 8000;

export function radioView({
  me,
  sync,
  receiver,
  clock,
  pack,
  investigation,
  ghosts,
  speech,
  onAbandon,
  onLoadSlot,
}: RadioContext): View {
  const progress = sync.progress.get();

  // The world clock readout. It ticks on its own — the world does not stop for the player, it
  // only pauses when they put the receiver down.
  const clockReadout = h('span', {
    class: 'nb-worldclock',
    'aria-label': t('radio.clock.label'),
    'aria-live': 'off',
  });
  const renderClock = (): void => {
    const time = clock.time();
    clockReadout.textContent = t('radio.clock', {
      day: time.game.day,
      time: formatGameClock(time.game),
    });
  };
  renderClock();
  const clockTimer = setInterval(renderClock, 1000);

  const frequencyReadout = h('output', {
    class: 'nb-readout__value',
    text: formatKhz(progress.frequencyKhz),
  });

  const bandMinKhz = pack?.band.minKhz ?? BAND_MIN_KHZ;
  const bandMaxKhz = pack?.band.maxKhz ?? BAND_MAX_KHZ;

  // Chapter 7: the operator's card watches the world rather than the player's clicks, so the only
  // things it needs told are the ones that leave no trace in the save — how far the dial has
  // actually been turned, and whether the case notes have ever been opened.
  let dialTravelKhz = 0;
  let lastDialKhz = progress.frequencyKhz;
  let notesUsed = false;

  const tuningKnob = createKnob({
    mode: 'encoder',
    minKhz: bandMinKhz,
    maxKhz: bandMaxKhz,
    valueKhz: progress.frequencyKhz,
    label: t('a11y.knob.frequency'),
    onInput: (khz) => {
      // Distance travelled, not distance from where we started: a player who sweeps out and back
      // has used the dial, and the card should not decide otherwise on a technicality.
      dialTravelKhz += Math.abs(khz - lastDialKhz);
      lastDialKhz = khz;
      frequencyReadout.textContent = formatKhz(khz);
      receiver.setFrequency(khz);
      ghosts?.relay.tune(khz);
      sync.patch((current) => ({
        ...current,
        frequencyKhz: khz,
        lastPlayedAt: new Date().toISOString(),
      }));
    },
    // Tuning fires continuously; the sync layer debounces, and this makes sure the last
    // position is written even if the player stops mid-debounce.
    onCommit: () => void sync.flush(),
  });

  // What the receiver has locked onto. Names come from the pack, already resolved for the
  // active locale by the loader.
  const receivingLine = h('p', {
    class: 'nb-receiving',
    'aria-live': 'polite',
    text: t('radio.deadAir'),
  });
  const renderReceiving = (signal: ActiveSignal | null): void => {
    receivingLine.textContent = signal
      ? t('radio.receiving', { label: signal.signal.label ?? signal.signal.id })
      : t('radio.deadAir');
    receivingLine.dataset.kind = signal?.signal.kind ?? 'none';
  };

  /**
   * Keep the caption in step with the world.
   *
   * Deliberately not driven by `onSignalChange`, which only fires when the signal *identity*
   * changes: switching the set on over dead air changes nothing about the tuned signal (it was
   * null and stays null), so a caption written on that event would still read "the set is off"
   * while the player was listening to static. It is polled instead, and `setState` is idempotent
   * so a poll costs the live region nothing.
   */
  const refreshCaption = (): void => {
    captions.setState(
      receiver.isPowered
        ? describeSignal(receiver.tunedSignal?.signal ?? null, t)
        : t('caption.off'),
    );
  };

  /**
   * The caption line.
   *
   * A polite live region so it is announced when it changes rather than interrupting, and marked
   * `hidden` when captions are off so it costs a screen reader nothing at all.
   */
  const captions = new CaptionChannel();
  const captionLine = h('p', {
    class: 'nb-caption',
    role: 'status',
    'aria-live': 'polite',
    'aria-label': t('caption.title'),
    hidden: !progress.settings.captions,
  });

  // A ghost has no other trace: no log entry, no clue, nothing on screen. Without this it simply
  // does not happen for a player using captions.
  const unsubscribeGhosts = ghosts?.onArrival(() => {
    if (!sync.progress.get().settings.muteGhosts) captions.push(t('caption.ghost'));
  });

  const unsubscribeCaption = captions.caption.subscribe((caption) => {
    // The transient goes after the state, never instead of it: hearing a ghost while tuned to a
    // station must not leave a player believing the station stopped.
    captionLine.textContent = [caption.state, caption.transient].filter(Boolean).join('  ·  ');
  }, true);

  const signalMeter = h('div', {
    class: 'nb-meter',
    role: 'meter',
    'aria-label': t('radio.signal'),
    'aria-valuemin': '0',
    'aria-valuemax': '100',
    'aria-valuenow': '0',
  });
  const signalFill = h('div', { class: 'nb-meter__fill' });
  signalMeter.appendChild(signalFill);

  const renderShape = (shape: { strength: number }): void => {
    const percent = Math.round(shape.strength * 100);
    signalFill.style.width = `${percent}%`;
    signalMeter.setAttribute('aria-valuenow', String(percent));
    signalMeter.dataset.locked = String(percent >= 85);
    tuningKnob.setStrength?.(shape.strength);
  };

  const volumeInput = h('input', {
    class: 'nb-fader__input',
    id: 'nb-volume',
    type: 'range',
    min: '0',
    max: '100',
    step: '1',
    value: String(Math.round(progress.settings.masterVolume * 100)),
    'aria-label': t('radio.volume'),
  }) as HTMLInputElement;

  volumeInput.addEventListener('input', () => {
    const masterVolume = Number(volumeInput.value) / 100;
    receiver.setVolume(masterVolume);
    sync.patch((current) => ({ ...current, settings: { ...current.settings, masterVolume } }));
  });

  // The fader is the one control that stays a slider, because that is what it is on the set too:
  // a cap running in a slot. The slot, the ticks and the grooves are the wrapper's job.
  const volumeFader = h(
    'div',
    { class: 'nb-fader' },
    h('span', { class: 'nb-fader__slot', 'aria-hidden': 'true' }),
    volumeInput,
  );

  /* --- Chapter 7 accessibility controls ------------------------------------------------------ */

  const captionToggle = h('button', {
    class: 'nb-button nb-button--quiet nb-button--small',
    type: 'button',
    id: 'nb-captions',
    'aria-pressed': String(progress.settings.captions),
  }) as HTMLButtonElement;

  const renderCaptionToggle = (): void => {
    const on = sync.progress.get().settings.captions;
    captionToggle.textContent = on ? t('radio.captions.on') : t('radio.captions.off');
    captionToggle.setAttribute('aria-pressed', String(on));
    captionLine.hidden = !on;
  };

  captionToggle.addEventListener('click', () => {
    const captions = !sync.progress.get().settings.captions;
    sync.patch((current) => ({ ...current, settings: { ...current.settings, captions } }));
    renderCaptionToggle();
  });
  // Drawn once on entry, the way the ghost toggle beside it already was. Without this the button
  // has no text at all until something else happens to redraw it — survivable when it sat on the
  // panel next to a label, plainly broken now that it is a row of its own in Settings.
  renderCaptionToggle();

  const clarityKnob = createKnob({
    mode: 'pot',
    value: progress.settings.voiceClarity,
    label: t('radio.clarity'),
    onInput: (voiceClarity) => {
      // Takes effect on the next line spoken, and on everything after it.
      speech?.setClarity(voiceClarity);
      sync.patch((current) => ({ ...current, settings: { ...current.settings, voiceClarity } }));
    },
  });

  const interferenceKnob = createKnob({
    mode: 'pot',
    value: progress.settings.atmospherics,
    label: t('radio.atmospherics'),
    onInput: (atmospherics) => {
      // Sudden sharp transients are exactly what some players need less of, and turning the
      // lightning down should not mean turning the band off — so this scales the crackle only.
      receiver.setAtmospherics(atmospherics);
      sync.patch((current) => ({ ...current, settings: { ...current.settings, atmospherics } }));
    },
  });

  const ghostToggle = h('button', {
    class: 'nb-button nb-button--quiet nb-button--small',
    type: 'button',
    id: 'nb-ghosts',
    'aria-pressed': String(progress.settings.muteGhosts),
  }) as HTMLButtonElement;

  const renderGhostToggle = (): void => {
    const muted = sync.progress.get().settings.muteGhosts;
    ghostToggle.textContent = muted ? t('radio.ghosts.off') : t('radio.ghosts.on');
    ghostToggle.setAttribute('aria-pressed', String(muted));
    ghostToggle.dataset.muted = String(muted);
  };

  ghostToggle.addEventListener('click', () => {
    const muteGhosts = !sync.progress.get().settings.muteGhosts;
    ghosts?.player.setMuted(muteGhosts);
    sync.patch((current) => ({
      ...current,
      settings: { ...current.settings, muteGhosts },
    }));
    renderGhostToggle();
    renderCaptionToggle();
  });
  renderGhostToggle();
  ghosts?.player.setMuted(progress.settings.muteGhosts);

  const effectsSelect = h(
    'select',
    { class: 'nb-select', id: 'nb-effects', 'aria-label': t('radio.effects') },
    ...(['full', 'reduced', 'off'] as const).map((value) =>
      h('option', {
        value,
        text: t(`radio.effects.${value}`),
        selected: progress.settings.effects === value,
      }),
    ),
  ) as HTMLSelectElement;

  effectsSelect.addEventListener('change', () => {
    const effects = effectsSelect.value as EffectsIntensity;
    document.documentElement.dataset.nbEffects = effects;
    refreshOverlay();
    sync.patch((current) => ({ ...current, settings: { ...current.settings, effects } }));
  });

  const statusLine = h('p', {
    class: 'nb-status-line',
    'aria-live': 'polite',
    text: t('radio.status.standby'),
  });

  /**
   * The power rocker.
   *
   * An old-school red cap sitting in a recessed well, thrown left for off and right for on. It is
   * a button underneath — the click, the disable-while-switching and the first-run mark are the
   * same ones the old text button had — but it announces as a switch, because that is what it is.
   * The OFF/ON engraving is decorative; `aria-checked` carries the state.
   */
  const powerSwitch = h(
    'button',
    {
      class: 'nb-switch',
      id: 'nb-power',
      type: 'button',
      role: 'switch',
      'aria-checked': 'false',
      'aria-pressed': 'false',
      'aria-label': t('a11y.power'),
    },
    h(
      'span',
      { class: 'nb-switch__well', 'aria-hidden': 'true' },
      h('span', { class: 'nb-switch__mark nb-switch__mark--off', text: t('a11y.switch.off') }),
      h('span', { class: 'nb-switch__mark nb-switch__mark--on', text: t('a11y.switch.on') }),
      h('span', { class: 'nb-switch__cap' }),
    ),
  ) as HTMLButtonElement;

  let updatePowerTransmitter: (() => void) | null = null;

  const renderPower = (): void => {
    const on = receiver.isPowered;
    powerSwitch.setAttribute('aria-checked', String(on));
    powerSwitch.setAttribute('aria-pressed', String(on));
    statusLine.textContent = on
      ? t(receiver.noiseMode === 'buffer' ? 'radio.noise.buffer' : 'radio.noise.worklet')
      : t('radio.status.standby');
    updatePowerTransmitter?.();
  };

  powerSwitch.addEventListener('click', () => {
    powerSwitch.disabled = true;
    const action = receiver.isPowered ? Promise.resolve(receiver.powerOff()) : receiver.powerOn();
    void action.finally(() => {
      powerSwitch.disabled = false;
      renderPower();
      // The card is only ever offered once per account. Marking it seen the first time the set is
      // switched on — rather than when the card is finished — means a player who quits halfway
      // through is not taught again, which is the right way round: they have met the set.
      if (receiver.isPowered && !sync.progress.get().onboarding.seen) {
        sync.patch((current) => ({
          ...current,
          onboarding: { ...current.onboarding, seen: true },
        }));
      }
      refreshCard();
    });
  });

  const syncStatus = h('span', {
    class: 'nb-status',
    'aria-live': 'polite',
    text: t('radio.saved'),
  });
  const unsubscribeSync = sync.syncState.subscribe((state) => {
    syncStatus.textContent =
      state === 'syncing'
        ? t('radio.saving')
        : state === 'offline'
          ? t('radio.savedOffline')
          : t('radio.saved');
    syncStatus.dataset.state = state;
  }, true);

  const unsubscribeProgress = sync.progress.subscribe((value) => {
    // Keeps the UI honest when the server copy wins a conflict (another device moved the dial).
    if (value.frequencyKhz !== receiver.frequencyKhz) {
      tuningKnob.setValue(value.frequencyKhz);
      frequencyReadout.textContent = formatKhz(value.frequencyKhz);
      receiver.setFrequency(value.frequencyKhz);
    }
  });

  /* ---- story surfaces -------------------------------------------------------------------- */

  // A short-lived line under the receiver announcing what just landed. A clue that arrived
  // silently would be indistinguishable from nothing happening.
  const toast = h('p', { class: 'nb-toast', role: 'status', 'aria-live': 'polite', hidden: true });
  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  const announce = (message: string): void => {
    toast.textContent = message;
    toast.hidden = false;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 6000);
  };

  const announceClues = (clueIds: string[]): void => {
    if (!investigation || clueIds.length === 0) return;
    const labels = clueIds
      .map((id) => investigation.view.get().clues.find((clue) => clue.id === id)?.label ?? id)
      .join(', ');
    announce(t('toast.clue', { label: labels }));
    notesPanel?.show('clues');
  };

  const notesPanel = investigation
    ? createInvestigationPanel({
        investigation,
        onInteract: () => {
          notesUsed = true;
        },
        onTune: (khz) => {
          notesUsed = true;
          tuningKnob.setValue(khz);
          frequencyReadout.textContent = formatKhz(khz);
          receiver.setFrequency(khz);
          sync.patch((current) => ({ ...current, frequencyKhz: khz }));
        },
      })
    : null;

  // Universal transceiver: Push-To-Talk is active across the entire radio on all channels.
  // When transmitting, voice audio is offered to the ghost relay (15% leak probability across same-band listeners),
  // speech is transcribed via STT, and channels respond according to their story state.
  const recorder = ghosts && isCaptureSupported() ? new FragmentRecorder() : null;
  let recording = false;

  // One word on the key, the way it would be stamped on the set. What to *do* with it is on the
  // operator's card, not engraved into the hardware.
  const talkButton = h('button', {
    class: 'nb-button nb-transmit__key',
    type: 'button',
    text: t('transmit.label'),
  }) as HTMLButtonElement;

  const meter = h('span', { class: 'nb-transmit__meter', 'aria-hidden': 'true' });
  const meterFill = h('span', { class: 'nb-transmit__fill' });
  meter.appendChild(meterFill);

  const heard = h('p', { class: 'nb-transmit__heard', 'aria-live': 'polite' });
  const status = h('p', { class: 'nb-transmit__status', role: 'status', 'aria-live': 'polite' });

  const voiceRow = h(
    'div',
    { class: 'nb-transmit' },
    talkButton,
    meter,
    h('div', { class: 'nb-transmit__readout' }, heard, status),
  );

  let matching = false;
  let countdown: ReturnType<typeof setInterval> | null = null;
  let clearTimer: ReturnType<typeof setTimeout> | null = null;

  const setStatus = (message: string, kind: 'idle' | 'busy' | 'warn' = 'idle'): void => {
    status.textContent = message;
    status.dataset.kind = kind;
  };

  const renderTransmitState = (state: TransmitState): void => {
    talkButton.dataset.phase = state.phase;
    talkButton.disabled =
      matching || !receiver.isPowered || (!state.canTransmit && state.phase !== 'transmitting');

    if (state.phase === 'transmitting') {
      const total = pushToTalk.rules.limits.maxTransmitMs ?? 1;
      meterFill.style.width = `${Math.round((state.remainingMs / total) * 100)}%`;
      meter.dataset.active = 'true';
      talkButton.textContent = t('transmit.transmitting');
      return;
    }

    meter.dataset.active = 'false';
    meterFill.style.width = '0%';

    if (state.phase === 'cooldown' || state.phase === 'overheated') {
      const seconds = Math.ceil(state.waitMs / 1000);
      talkButton.textContent =
        state.phase === 'overheated'
          ? t('transmit.overheated', { seconds })
          : t('transmit.cooling', { seconds });
      return;
    }

    talkButton.textContent = matching ? t('transmit.matching') : t('transmit.label');
  };

  const startCountdown = (): void => {
    if (countdown !== null) return;
    countdown = setInterval(() => {
      const state = pushToTalk.state();
      renderTransmitState(state);
      if (state.phase === 'idle' && countdown !== null) {
        clearInterval(countdown);
        countdown = null;
      }
    }, 200);
  };

  const pushToTalk = new PushToTalk({
    recogniser: createRecogniser(locale()),
    rules: new TransmitRules(pack ? { limits: pack.transmit } : {}),
    events: {
      onCutOff: () => announce(t('transmit.cutOff')),
      onPartial: (text) => {
        heard.textContent = t('transmit.heard', { text });
      },
      onState: (state) => {
        renderTransmitState(state);
        if (state.phase === 'transmitting') {
          receiver.setTransmitting(true);
          if (!recording && recorder) {
            recording = true;
            void recorder.start().then((ok) => {
              if (!ok) recording = false;
            });
          }
        } else {
          receiver.setTransmitting(false);
        }
      },
    },
  });

  updatePowerTransmitter = () => {
    if (!receiver.isPowered) {
      pushToTalk.reset();
      heard.textContent = '';
      setStatus('');
    }
    renderTransmitState(pushToTalk.state());
  };

  const handleOutcome = async (outcome: TransmitOutcome): Promise<void> => {
    if (clearTimer !== null) {
      clearTimeout(clearTimer);
      clearTimer = null;
    }

    // Always offer recorded fragment to ghost relay on ALL channels:
    if (recorder && recording && ghosts) {
      recording = false;
      void recorder.stop().then((fragment) => {
        if (!fragment) return;
        ghosts.relay.offer({
          khz: Math.round(receiver.frequencyKhz),
          durationMs: fragment.durationMs,
          mime: fragment.mime,
          audio: fragment.audio,
          ...(outcome.kind === 'transcript' ? { sttText: outcome.transcript } : {}),
        });
      });
    }

    if (outcome.kind === 'refused') {
      setStatus(t('transmit.tooSoon'), 'warn');
      startCountdown();
      return;
    }

    if (outcome.kind === 'error') {
      setStatus(t(`transmit.error.${outcome.error.kind}` as never), 'warn');
      return;
    }

    const current = investigation?.view.get().dialogue;
    const signal = receiver.tunedSignal;

    if (outcome.kind === 'silent') {
      if (current) {
        setStatus(t('transmit.nothingHeard'), 'warn');
      } else if (signal && investigation && investigation.canOpenDialogue(signal)) {
        if (investigation.dialogueInitiation(signal) === 'user') {
          setStatus(t('transmit.hailing'), 'busy');
          investigation.openDialogue(signal);
          refreshTransmit(receiver.tunedSignal);
        } else {
          investigation.openDialogue(signal);
          refreshTransmit(receiver.tunedSignal);
        }
      } else {
        setStatus(t('transmit.emptyChannel'), 'idle');
        clearTimer = setTimeout(() => {
          setStatus('');
        }, 3500);
      }
      startCountdown();
      return;
    }

    // outcome.kind === 'transcript'
    heard.textContent = t('transmit.heard', { text: outcome.transcript });

    if (current) {
      matching = true;
      setStatus(t('transmit.matching'), 'busy');
      renderTransmitState(pushToTalk.state());

      try {
        const result = await api.matchUtterance(
          outcome.transcript,
          current.node.options.map((option) => ({
            optionId: option.id,
            label: option.label,
            ...(option.intents.length > 0 ? { intents: option.intents } : {}),
          })),
        );

        if (result.optionId) {
          const gained = investigation!.chooseOption(result.optionId);
          if (gained.length > 0) announceClues(gained);
          setStatus('');
          heard.textContent = '';
        } else {
          setStatus(t('transmit.unmatched'), 'warn');
        }
      } catch {
        setStatus(t('transmit.offline'), 'warn');
      } finally {
        matching = false;
        startCountdown();
        renderTransmitState(pushToTalk.state());
      }
      return;
    }

    if (investigation && signal && investigation.canOpenDialogue(signal)) {
      if (investigation.dialogueInitiation(signal) === 'user') {
        setStatus(t('transmit.hailing'), 'busy');
        investigation.openDialogue(signal);
        refreshTransmit(receiver.tunedSignal);
      } else {
        investigation.openDialogue(signal);
        refreshTransmit(receiver.tunedSignal);
      }
    } else {
      setStatus(t('transmit.emptyChannel'), 'idle');
      clearTimer = setTimeout(() => {
        setStatus('');
      }, 3500);
    }
    startCountdown();
  };

  const beginTransmit = (): void => {
    if (!receiver.isPowered || matching) return;
    if (clearTimer !== null) {
      clearTimeout(clearTimer);
      clearTimer = null;
    }
    heard.textContent = '';
    setStatus('');
    void pushToTalk.press().then(handleOutcome);
  };

  const endTransmit = (): void => {
    pushToTalk.release();
  };

  talkButton.addEventListener('pointerdown', (event: PointerEvent) => {
    event.preventDefault();
    talkButton.setPointerCapture(event.pointerId);
    beginTransmit();
  });
  talkButton.addEventListener('pointerup', endTransmit);
  talkButton.addEventListener('pointercancel', endTransmit);

  talkButton.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    if (!event.repeat) beginTransmit();
  });
  talkButton.addEventListener('keyup', (event: KeyboardEvent) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    endTransmit();
  });

  const onGlobalKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Space' || event.repeat) return;
    const target = event.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    if (!receiver.isPowered || pushToTalk.isTransmitting) return;
    event.preventDefault();
    beginTransmit();
  };

  const onGlobalKeyUp = (event: KeyboardEvent): void => {
    if (event.code !== 'Space') return;
    const target = event.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    if (!pushToTalk.isTransmitting) return;
    event.preventDefault();
    endTransmit();
  };

  window.addEventListener('keydown', onGlobalKeyDown);
  window.addEventListener('keyup', onGlobalKeyUp);
  window.addEventListener('blur', endTransmit);

  const dialoguePanel = investigation
    ? createDialoguePanel({
        investigation,
        onClues: announceClues,
        pushToTalk,
        speech,
        showVoiceRow: false,
      })
    : null;

  // "Answer" / "Hail" appears when the signal in front of the player carries a conversation
  // they have not finished, offering a clickable alternative to voice transmission.
  const transmitButton = h('button', {
    class: 'nb-button nb-button--primary',
    type: 'button',
    text: t('dialogue.transmit'),
    hidden: true,
  }) as HTMLButtonElement;

  transmitButton.addEventListener('click', () => {
    const signal = receiver.tunedSignal;
    if (!investigation || !signal || !investigation.openDialogue(signal)) {
      announce(t('toast.noAnswer'));
      refreshTransmit(receiver.tunedSignal);
      return;
    }
    refreshTransmit(receiver.tunedSignal);
  });

  const refreshTransmit = (signal: ActiveSignal | null): void => {
    if (!investigation || signal === null || !investigation.canOpenDialogue(signal)) {
      transmitButton.hidden = true;
      return;
    }
    const initiation = investigation.dialogueInitiation(signal);
    transmitButton.textContent =
      initiation === 'user' ? t('transmit.hail') : t('dialogue.transmit');
    transmitButton.hidden = false;
  };

  /**
   * The story clock: tell the investigation what the receiver is holding, so dwell time
   * accumulates. Also triggers NPC-initiated broadcasts on dwell.
   */
  const storyTimer = investigation
    ? setInterval(() => {
        investigation.breakOffIfGone(receiver.tunedSignal, receiver.frequencyKhz);
        const gained = investigation.onTuned(receiver.tunedSignal, clock.time());
        if (gained.length > 0) announceClues(gained);

        const signal = receiver.tunedSignal;
        if (
          signal &&
          investigation.canOpenDialogue(signal) &&
          investigation.dialogueInitiation(signal) === 'npc'
        ) {
          // NPC broadcasts into channel first (Halvard):
          investigation.openDialogue(signal);
        }
        refreshTransmit(receiver.tunedSignal);
      }, 500)
    : null;

  let announcedResolution = false;
  const unsubscribeResolution = investigation
    ? investigation.view.subscribe((view) => {
        if (view.ending && !announcedResolution) {
          announcedResolution = true;
          announce(t('toast.resolved'));
          // The author's epilogue, if one was recorded, spoken once and in the clear — it is not
          // a transmission, so it skips the radio chain. Missing audio is silent, as everywhere.
          if (view.ending.resolvedClipUrl) {
            void speech?.play(view.ending.resolvedClipUrl, { dry: true });
          }
        }

        // Why the conversation panel just vanished. Without this the player is left wondering
        // whether they broke something, which is the wrong kind of unsettling.
        if (view.interrupted) {
          announce(
            view.interrupted.reason === 'moved'
              ? t('dialogue.movedOn', { label: view.interrupted.label })
              : t('dialogue.tunedAway', { label: view.interrupted.label }),
          );
          investigation.acknowledgeInterruption();
        }
      }, true)
    : null;

  const card = createOperatorCard({
    onDismiss: () => {
      sync.patch((current) => ({
        ...current,
        onboarding: { ...current.onboarding, dismissed: true },
      }));
      refreshCard();
    },
  });

  /**
   * A returning player is never taught anything.
   *
   * Read once, from the save as it was when the view was built — not live. `seen` is set the
   * moment the set is switched on, so reading it live would hide the card a tick after it
   * appeared, on the player's very first night.
   */
  const firstRun = !progress.onboarding.seen;

  const refreshCard = (): void => {
    card.update({
      snapshot: {
        powered: receiver.isPowered,
        dialTravelKhz,
        logged: investigation?.view.get().log.length ?? 0,
        notesUsed,
        spoken: investigation
          ? Object.keys(sync.progress.get().investigation.dialogue).length > 0
          : false,
      },
      dismissed: sync.progress.get().onboarding.dismissed,
      firstRun,
    });
  };

  // The card has its own timer rather than riding the story poll: a pack that tells no story has
  // no story timer, and the set still has to teach someone how to use it.
  const cardTimer = setInterval(() => {
    refreshCard();
    refreshCaption();
  }, 500);
  refreshCard();
  refreshCaption();

  // The save is the authority for the audio settings too. Applied on entry so a player who
  // turned the crackle down or the clarity up does not get the defaults back next session.
  receiver.setAtmospherics(progress.settings.atmospherics);
  speech?.setClarity(progress.settings.voiceClarity);

  /* ---- Save menu + Abandon (top bar) ---------------------------------------------------- */

  // The in-game Save menu: the four slots in `'ingame'` mode (save-here / load / delete), in a
  // dismissible, focus-trapped overlay. Load routes through the app so the game rebuilds.
  const saveSlots = createSaveSlots({ mode: 'ingame', sync, onLoadSlot });

  let lastFocused: HTMLElement | null = null;

  const closeSaveButton = h('button', {
    class: 'nb-button nb-button--quiet nb-button--small',
    type: 'button',
    text: t('radio.saveMenu.close'),
    onclick: () => closeSaveMenu(),
  }) as HTMLButtonElement;

  const saveDialog = h(
    'div',
    {
      class: 'nb-savemenu__dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': t('radio.saveMenu.title'),
    },
    h(
      'header',
      { class: 'nb-savemenu__head' },
      h(
        'div',
        { class: 'nb-savemenu__titles' },
        h('h2', { class: 'nb-savemenu__title', text: t('radio.saveMenu.title') }),
        h('p', { class: 'nb-lede', text: t('radio.saveMenu.lede') }),
      ),
      closeSaveButton,
    ),
    saveSlots.element,
  );

  const saveOverlay = h(
    'div',
    { class: 'nb-savemenu', hidden: true },
    // A tap on the scrim closes the menu, matching the game's other dismissible surfaces.
    h('div', { class: 'nb-savemenu__scrim', onclick: () => closeSaveMenu() }),
    saveDialog,
  );

  const onSaveMenuKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSaveMenu();
      return;
    }
    if (event.key !== 'Tab') return;
    // Keep focus inside the dialog while it is open.
    const focusable = saveDialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const activeEl = document.activeElement as HTMLElement | null;
    if (event.shiftKey && activeEl === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openSaveMenu = (): void => {
    lastFocused = document.activeElement as HTMLElement | null;
    saveOverlay.hidden = false;
    saveSlots.refresh();
    document.addEventListener('keydown', onSaveMenuKeyDown);
    closeSaveButton.focus();
  };

  const closeSaveMenu = (): void => {
    if (saveOverlay.hidden) return;
    saveOverlay.hidden = true;
    document.removeEventListener('keydown', onSaveMenuKeyDown);
    lastFocused?.focus();
  };

  const saveButton = h('button', {
    class: 'nb-button nb-button--quiet nb-button--small',
    type: 'button',
    text: t('radio.save'),
    onclick: () => openSaveMenu(),
  }) as HTMLButtonElement;

  /* ---- Settings modal (gear, top bar) ---------------------------------------------------- */

  // Effects, ghost signals and captions used to sit on the front panel between the volume and the
  // clarity control, which put three application preferences among the set's hardware. They are
  // the same three controls with the same handlers — only the surface they are mounted on moved.
  let settingsLastFocused: HTMLElement | null = null;

  const closeSettingsButton = h('button', {
    class: 'nb-button nb-button--quiet nb-button--small',
    type: 'button',
    text: t('radio.settings.close'),
    onclick: () => closeSettings(),
  }) as HTMLButtonElement;

  const settingsDialog = h(
    'div',
    {
      class: 'nb-savemenu__dialog nb-settings__dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': t('radio.settings.title'),
    },
    h(
      'header',
      { class: 'nb-savemenu__head' },
      h(
        'div',
        { class: 'nb-savemenu__titles' },
        h('h2', { class: 'nb-savemenu__title', text: t('radio.settings.title') }),
        h('p', { class: 'nb-lede', text: t('radio.settingsHint') }),
      ),
      closeSettingsButton,
    ),
    h(
      'div',
      { class: 'nb-settings__rows' },
      control(t('radio.effects'), 'nb-effects', effectsSelect),
      control(t('radio.ghosts'), 'nb-ghosts', ghostToggle),
      control(t('radio.captions'), 'nb-captions', captionToggle),
    ),
  );

  const settingsOverlay = h(
    'div',
    { class: 'nb-savemenu nb-settings', hidden: true },
    h('div', { class: 'nb-savemenu__scrim', onclick: () => closeSettings() }),
    settingsDialog,
  );

  const onSettingsKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSettings();
      return;
    }
    if (event.key !== 'Tab') return;
    // Keep focus inside the dialog while it is open.
    const focusable = settingsDialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const activeEl = document.activeElement as HTMLElement | null;
    if (event.shiftKey && activeEl === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openSettings = (): void => {
    settingsLastFocused = document.activeElement as HTMLElement | null;
    settingsOverlay.hidden = false;
    settingsButton.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onSettingsKeyDown);
    closeSettingsButton.focus();
  };

  const closeSettings = (): void => {
    if (settingsOverlay.hidden) return;
    settingsOverlay.hidden = true;
    settingsButton.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onSettingsKeyDown);
    settingsLastFocused?.focus();
  };

  const settingsButton = h(
    'button',
    {
      class: 'nb-button nb-button--quiet nb-button--small nb-bar__gear',
      type: 'button',
      'aria-label': t('radio.settings'),
      'aria-haspopup': 'dialog',
      'aria-expanded': 'false',
      onclick: () => openSettings(),
    },
    gearIcon(),
  ) as HTMLButtonElement;

  // "Abandon" replaces the old Sign Out: it discards the live autosave (after a two-tap confirm)
  // and returns to the Main Menu, rather than ending the whole session.
  const abandonButton = h('button', {
    class: 'nb-button nb-button--quiet nb-button--small',
    type: 'button',
    text: t('radio.abandon'),
  }) as HTMLButtonElement;

  const abandonConfirm = h(
    'div',
    {
      class: 'nb-slot__confirm',
      role: 'alertdialog',
      'aria-label': t('radio.confirmAbandon'),
      hidden: true,
    },
    h('span', { class: 'nb-slot__confirm-q', text: t('radio.confirmAbandon') }),
    h('button', {
      class: 'nb-button nb-button--alert nb-button--small',
      type: 'button',
      text: t('menu.slot.confirmYes'),
      onclick: () => {
        abandonConfirm.hidden = true;
        onAbandon();
      },
    }),
    h('button', {
      class: 'nb-button nb-button--quiet nb-button--small',
      type: 'button',
      text: t('menu.slot.confirmNo'),
      onclick: () => {
        abandonConfirm.hidden = true;
      },
    }),
  );

  abandonButton.addEventListener('click', () => {
    abandonConfirm.hidden = false;
  });

  const element = h(
    'div',
    { class: 'nb-page nb-page--radio' },
    h(
      'header',
      { class: 'nb-bar' },
      h('span', { class: 'nb-bar__mark' }, brandLogo('nb-bar__logo', t('app.name'))),
      clockReadout,
      h('span', { class: 'nb-bar__spacer' }),
      syncStatus,
      h('span', { class: 'nb-bar__account', text: me.account?.email ?? '' }),
      settingsButton,
      saveButton,
      abandonButton,
    ),
    abandonConfirm,
    saveOverlay,
    settingsOverlay,
    h(
      'main',
      { class: 'nb-radio', id: 'main' },
      h(
        'section',
        { class: 'nb-receiver' },
        h(
          'div',
          { class: 'nb-readout' },
          h('span', { class: 'nb-readout__label', text: t('radio.frequency') }),
          frequencyReadout,
          h('span', { class: 'nb-readout__unit', text: 'kHz' }),
        ),
        h(
          'div',
          { class: 'nb-tuner' },
          tuningKnob.element,
          h('p', { class: 'nb-dial__hint', text: t('radio.dialHint') }),
        ),
        h(
          'div',
          { class: 'nb-signal' },
          h('span', { class: 'nb-readout__label', text: t('radio.signal') }),
          signalMeter,
          receivingLine,
        ),
        h(
          'div',
          { class: 'nb-controls' },
          control(t('radio.power'), 'nb-power', powerSwitch),
          control(t('radio.volume'), 'nb-volume', volumeFader),
          // The knobs carry their own `aria-label`, so an engraved panel legend is all they want
          // beside them — a `<label for>` pointing at a div would resolve to nothing.
          instrument(t('radio.clarity'), clarityKnob.element),
          instrument(t('radio.atmospherics'), interferenceKnob.element),
        ),
        voiceRow,
        captionLine,
        statusLine,
        h('p', {
          class: 'nb-status-line nb-band-line',
          text: t('radio.band', { min: bandMinKhz, max: bandMaxKhz }),
        }),
        transmitButton,
        toast,
      ),
      card.element,
      ...(dialoguePanel ? [dialoguePanel.element] : []),
      ...(notesPanel ? [notesPanel.element] : []),
    ),
    footer(),
  );

  renderPower();
  const unsubscribeShape = receiver.onShapeChange(renderShape);
  const unsubscribeSignal = receiver.onSignalChange((signal) => {
    renderReceiving(signal);
    refreshTransmit(signal);
  });

  return {
    element,
    dispose: () => {
      window.removeEventListener('keydown', onGlobalKeyDown);
      window.removeEventListener('keyup', onGlobalKeyUp);
      window.removeEventListener('blur', endTransmit);
      document.removeEventListener('keydown', onSaveMenuKeyDown);
      document.removeEventListener('keydown', onSettingsKeyDown);
      if (countdown) clearInterval(countdown);
      if (clearTimer) clearTimeout(clearTimer);
      unsubscribeSync();
      unsubscribeProgress();
      unsubscribeShape();
      unsubscribeSignal();
      unsubscribeCaption();
      unsubscribeGhosts?.();
      captions.dispose();
      unsubscribeResolution?.();
      clearInterval(clockTimer);
      if (storyTimer) clearInterval(storyTimer);
      clearInterval(cardTimer);
      if (toastTimer) clearTimeout(toastTimer);
      notesPanel?.dispose();
      dialoguePanel?.dispose();
      pushToTalk?.dispose();
      recorder?.cancel();
      tuningKnob.dispose();
      clarityKnob.dispose();
      interferenceKnob.dispose();
      void sync.flush();
    },
  };
}

function control(label: string, forId: string, input: HTMLElement): HTMLElement {
  return h(
    'div',
    { class: 'nb-control' },
    h('label', { class: 'nb-control__label', for: forId, text: label }),
    input,
  );
}

/** A control whose accessible name it already carries itself — the legend is engraving, not a label. */
function instrument(label: string, element: HTMLElement): HTMLElement {
  return h(
    'div',
    { class: 'nb-control nb-control--instrument' },
    h('span', { class: 'nb-control__label', 'aria-hidden': 'true', text: label }),
    element,
  );
}

/** The gear on the header bar. Decorative — the button carries the name. */
function gearIcon(): HTMLElement {
  return h('span', {
    class: 'nb-icon nb-icon--gear',
    'aria-hidden': 'true',
    html:
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6"><circle cx="12" cy="12" r="3.2"/><path d="M12 2.6v3M12 18.4v3' +
      'M2.6 12h3M18.4 12h3M5.1 5.1l2.1 2.1M16.8 16.8l2.1 2.1M18.9 5.1l-2.1 2.1M7.2 16.8l-2.1 2.1"/>' +
      '</svg>',
  });
}

export function formatKhz(khz: number): string {
  return khz.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
